import { useAuth } from '../contexts/AuthContext';
import { useRoleContext } from '../contexts/RoleContext';

/**
 * Hook that combines auth context with role switcher override
 * When viewing as a different role, permission checks are overridden
 */
export function usePermissions() {
  const auth = useAuth();
  const roleContext = useRoleContext();
  
  // Get the effective role - either the view role or the actual role
  const effectiveRole = roleContext.viewRole;
  const actualRole = auth.user?.tenantRole;
  
  // Only apply role override if user is actually a TenantAdmin
  // This prevents non-admins from escalating their privileges
  const shouldOverride = actualRole === 'TenantAdmin' && effectiveRole !== actualRole;
  
  const canEditContact = (): boolean => {
    if (actualRole !== 'TenantAdmin') {
      // Non-admins always use their real permissions
      return auth.canEditContact();
    }
    
    // Admin can override: Manager and TenantAdmin can edit, User cannot
    return effectiveRole === 'TenantAdmin' || effectiveRole === 'Manager';
  };
  
  const canAccessSites = (): boolean => {
    if (actualRole !== 'TenantAdmin') {
      // Non-admins always use their real permissions
      return auth.canAccessSites();
    }
    
    // Admin can override: Only TenantAdmin and Manager can access sites
    return effectiveRole === 'TenantAdmin' || effectiveRole === 'Manager';
  };
  
  const canAccessAdmin = (): boolean => {
    if (actualRole !== 'TenantAdmin') {
      // Non-admins always use their real permissions
      return auth.canAccessAdmin();
    }
    
    // Admin can override: Only TenantAdmin can access admin
    return effectiveRole === 'TenantAdmin';
  };
  
  const canManageList = (listId: string): boolean => {
    if (actualRole !== 'TenantAdmin') {
      // Non-admins always use their real permissions
      return auth.canManageList(listId);
    }
    
    // Admin can override: TenantAdmin can manage all lists, others cannot
    return effectiveRole === 'TenantAdmin';
  };
  
  const hasPermission = (permission: string, resourceType?: 'site' | 'list', resourceId?: string): boolean => {
    if (actualRole !== 'TenantAdmin') {
      // Non-admins always use their real permissions
      return auth.hasPermission(permission, resourceType, resourceId);
    }
    
    // Admin can override: Simplified permission check based on effective role
    switch (effectiveRole) {
      case 'TenantAdmin':
        return true;
      case 'Manager':
        return permission.includes('read') || permission.includes('write') || permission.includes('manage');
      case 'User':
        return permission.includes('read') || permission.includes('create');
      default:
        return false;
    }
  };
  
  return {
    ...auth,
    canEditContact,
    canAccessSites,
    canAccessAdmin,
    canManageList,
    hasPermission,
    // Additional info
    isUsingRoleOverride: shouldOverride,
    effectiveRole,
    actualRole,
  };
}
